import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import React, { KeyboardEvent, createRef } from 'react';
import Async from 'react-select/async';

import SelectControl from '@commons/Select/SelectControl';
import SelectMenuList from '@commons/Select/SelectMenuList';
import SelectOption from '@commons/Select/SelectOption';
import SelectLoadingIndicator from '@commons/Select/SelectLoadingIndicator';
import { CustomSelectProps, Option, SelectRef } from '@commons/Select/types';
import { customStyles, filterOptions, isValidOption, optionsToMap } from '@commons/Select/utils';

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

export interface SelectProps<IsMulti extends boolean>
  extends Pick<
    CustomSelectProps<IsMulti>,
    | 'id'
    | 'options'
    | 'value'
    | 'onChange'
    | 'isDisabled'
    | 'isMulti'
    | 'placeholder'
    | 'selectedOptionIndex'
    | 'pageSize'
  > {
  loadingMessage: string;
  noOptionsMessage: string;
  isRTL: boolean;
}

interface SelectState {
  focusedOptionIndex: number;
  options: ReturnType<typeof optionsToMap>;
  inputValue: string;
  isUserNavigatingTheListWithKeyboard: boolean;
  menuIsOpen: boolean;
}

//----------------------------------------------------------------------
// COMPONENT
//----------------------------------------------------------------------

class Select<IsMulti extends boolean> extends React.Component<SelectProps<IsMulti>, SelectState> {
  //----------------------------------------------------------------------
  // PROPERTIES
  //----------------------------------------------------------------------

  selectRef = createRef<SelectRef>();

  static defaultProps = {
    pageSize: 7
  };

  //----------------------------------------------------------------------
  // LIFECYCLE
  //----------------------------------------------------------------------

  constructor(props: SelectProps<IsMulti>) {
    super(props);

    const options = optionsToMap(props.options);
    let focusedOptionIndex = 0;

    if (!isNil(props.value) && !Array.isArray(props.value)) {
      const scrollTargetIndex = options.get(props.value.value)?.idx ?? 0;
      focusedOptionIndex = scrollTargetIndex;
    }

    this.state = {
      focusedOptionIndex,
      options,
      inputValue: '',
      isUserNavigatingTheListWithKeyboard: false,
      menuIsOpen: false
    };
  }

  componentDidUpdate(prevProps: SelectProps<IsMulti>) {
    if (!isEqual(prevProps.options, this.props.options)) {
      const newOptions = optionsToMap(this.props.options);
      this.setState((currentState) => ({
        ...currentState,
        options: newOptions
      }));
    }
  }

  //----------------------------------------------------------------------
  // STATE
  //----------------------------------------------------------------------

  toggleKeyboardNavigation = (newState: boolean) => {
    this.setState({ isUserNavigatingTheListWithKeyboard: newState });
  };

  //----------------------------------------------------------------------
  // UTILS
  //----------------------------------------------------------------------

  selectOption = (option: Option) => {
    const selectInstance = this.selectRef.current;

    if (selectInstance) {
      selectInstance.selectOption(option);
    }
  };

  getFocusedOption() {
    const selectInstance = this.selectRef.current;

    if (selectInstance) {
      return selectInstance.state.focusedOption as Option;
    }

    return null;
  }

  selectFocusedOption() {
    const focusedOption = this.getFocusedOption();

    if (focusedOption) {
      this.selectOption(focusedOption);
    }
  }

  loadOptions: NonNullable<CustomSelectProps<IsMulti>['loadOptions']> = (
    inputValue,
    completionHandler
  ) => {
    const result = filterOptions(this.props.options)(inputValue);

    this.onOptionsChange(result);
    setTimeout(() => {
      completionHandler(result);
    }, 30)

  };

  resetOptions = () => {
    const originalOptions = optionsToMap(this.props.options);
    this.setState({ options: originalOptions });
  };

  //----------------------------------------------------------------------
  // EVENTS
  //----------------------------------------------------------------------

  onSpaceKeyDown: NonNullable<CustomSelectProps<IsMulti>['onKeyDown']> = (event) => {
    if (!this.state.menuIsOpen) {
      return;
    }

    if (this.state.isUserNavigatingTheListWithKeyboard) {
      this.selectFocusedOption();
      event.preventDefault();
    }
  };

  onKeyboardNavigation = (action: KeyboardEvent<HTMLDivElement>['key']) => {
    const focusedOption = this.getFocusedOption();

    if (this.state.menuIsOpen) {
      this.onFocusChange(focusedOption, action);
    }
  };

  onKeyDown: NonNullable<CustomSelectProps<IsMulti>['onKeyDown']> = (event) => {
    switch (event.key) {
      case ' ':
        this.onSpaceKeyDown(event);
        break;
      // Keyboard navigation keys
      case 'ArrowUp':
      case 'ArrowDown':
      case 'PageUp':
      case 'PageDown':
      case 'Home':
      case 'End':
        this.onKeyboardNavigation(event.key);
        this.toggleKeyboardNavigation(true);
        break;
      // Keys that close the menu either with selection or not
      case 'Escape':
      case 'Enter':
      case 'Tab':
        this.toggleKeyboardNavigation(false);
        break;
      // Explicitly considering these keys below as "doing something" on the input
      // In this case, they move the caret
      case 'ArrowLeft':
      case 'ArrowRight':
      // Explicitly considering these keys below as writing on the input
      case 'Delete':
      case 'Backspace':
        this.toggleKeyboardNavigation(false);
        break;
      // Every other key is considered as the user writing on the input
      default:
        this.toggleKeyboardNavigation(false);
        break;
    }
  };

  onMenuStateToggle = (newState: boolean) => () => {
    this.setState({ menuIsOpen: newState });
  };

  onFocusChange = (focusedOption: Option | null, action: KeyboardEvent<HTMLDivElement>['key']) => {
    const { options } = this.state;
    const pageSize = this.props.pageSize as number;

    const firstElementIndex = 0;
    const lastElementIndex = this.props.options.length;

    let focusedOptionIndex = firstElementIndex;

    // All the leyboard navigation keys defined via `onKeyDown` should be handled here.
    if (!isEmpty(options) && isValidOption(focusedOption)) {
      const focusedOptionIndexBase = options.get(focusedOption.value)!.idx;

      if (action === 'Home') {
        focusedOptionIndex = firstElementIndex;
      }

      if (action === 'End') {
        focusedOptionIndex = lastElementIndex;
      }

      if (action === 'PageUp') {
        focusedOptionIndex = focusedOptionIndexBase - pageSize;
      }

      if (action === 'PageDown') {
        focusedOptionIndex = focusedOptionIndexBase + pageSize;
      }

      if (action === 'ArrowUp' && focusedOptionIndexBase === 0) {
        focusedOptionIndex = lastElementIndex;
      }

      if (action === 'ArrowUp' && focusedOptionIndexBase > 0) {
        focusedOptionIndex = focusedOptionIndexBase - 1;
      }

      if (action === 'ArrowDown' && focusedOptionIndexBase < lastElementIndex - 1) {
        focusedOptionIndex = focusedOptionIndexBase + 1;
      }

      this.setState({ focusedOptionIndex });
    }
  };

  onOptionsChange = (newOptions: Array<Option>) => {
    const newOptionsMap = optionsToMap(newOptions);
    this.setState((prevState) => ({
      ...prevState,
      options: newOptionsMap
    }));
  };

  onInputChange: NonNullable<CustomSelectProps<IsMulti>['onInputChange']> = (newValue) => {
    this.setState({ inputValue: newValue });
    this.resetOptions();
    return newValue;
  };

  onChange: CustomSelectProps<IsMulti>['onChange'] = (newValue, actionMeta) => {
    this.resetOptions();
    // @ts-ignore
    this.props.onChange(newValue, actionMeta);
  };

  //----------------------------------------------------------------------
  // SUB-TREES
  //----------------------------------------------------------------------

  renderLoadingState = () => {
    return this.props.loadingMessage;
  };

  renderEmptyState = () => {
    return <span aria-atomic="true" aria-live="assertive" role="alert">{this.props.noOptionsMessage}</span>;
  };

  //----------------------------------------------------------------------
  // RENDER
  //----------------------------------------------------------------------

  render() {
    return (
      <Async
        {...this.props}
        // Options
        defaultOptions={this.props.options}
        loadOptions={this.loadOptions}
        // State
        menuIsOpen={this.state.menuIsOpen}
        inputValue={this.state.inputValue}
        focusedOptionIndex={this.state.focusedOptionIndex}
        selectedOptionIndex={this.props.selectedOptionIndex}
        // Custom Components
        styles={{ ...customStyles }}
        // @ts-ignore
        components={{ MenuList: SelectMenuList, Option: SelectOption, Control: SelectControl, LoadingIndicator: SelectLoadingIndicator }}
        // Alternative UI states
        loadingMessage={this.renderLoadingState}
        noOptionsMessage={this.renderEmptyState}
        // Custom Handlers
        onMenuOpen={this.onMenuStateToggle(true)}
        onMenuClose={this.onMenuStateToggle(false)}
        onInputChange={this.onInputChange}
        onKeyDown={this.onKeyDown}
        // @ts-ignore
        onChange={this.onChange}
        // Misc
        isRtl={this.props.isRTL}
        captureMenuScroll={false}
        isClearable
        backspaceRemovesValue
        blurInputOnSelect
        menuShouldScrollIntoView
        menuShouldBlockScroll
        // Issue: https://jira.medallia.com/browse/OA-28098
        // Workaround here: https://github.com/JedWatson/react-select/issues/3823
        form=""
        // Ref
        ref={this.selectRef}
      />
    );
  }
}

export default Select;
